import { useEffect, useState } from 'react'
import { api } from '../api'
import type { Progress, Topic } from '../types'
import { ApiError } from '../types'
import { accText } from '../components/ui'
import { Between, Label, Note, Screen, Shell } from '../components/shell'
import { Icon } from '../components/Icon'
import { TopicLine } from './setup'

export function ProgressView({
  topics,
  onGoWrong,
}: {
  topics: Topic[]
  onGoWrong: () => void
}) {
  const [p, setP] = useState<Progress | null>(null)
  const [err, setErr] = useState('')
  const [tab, setTab] = useState<'topic' | 'wrong'>('topic')

  useEffect(() => {
    let alive = true
    api
      .progress()
      .then((r) => alive && setP(r))
      .catch((e) => alive && setErr((e as ApiError).detail || 'Không tải được tiến độ.'))
    return () => {
      alive = false
    }
  }, [])

  // Chỉ hiện đề mục đã làm — đề mục chưa đụng tới thì thanh 0% chỉ làm màn
  // hình dài thêm mà không mang thông tin. Sắp theo tỉ lệ đúng tăng dần.
  const done = topics.filter((t) => t.attempts > 0).sort((a, b) => a.accuracy - b.accuracy)

  // peak là chiều cao cột cao nhất. Chia cho 1 thay vì Math.max() trên mảng
  // rỗng — `Math.max()` không có tham số trả -Infinity, và -Infinity làm
  // mọi phép chia ra NaN.
  const peak = p ? Math.max(1, ...p.by_day.map((d) => d.answered)) : 1

  return (
    <Shell>
      <Screen>
        <Between className="mb-3">
          <h1>Tiến độ của bạn</h1>
          {p && p.streak_days > 0 && (
            <span className="font-mono text-sm font-semibold text-gold">
              {p.streak_days} ngày liên tiếp
            </span>
          )}
        </Between>

        {err && (
          <div className="mb-3 rounded-lg bg-bad-soft px-3 py-2 text-sm text-bad">{err}</div>
        )}
        {!p && !err && <Note>Đang tải…</Note>}

        {p && (
          <>
            <div className="mb-3 flex rounded-xl bg-surface-2 p-1">
              <TabBtn on={tab === 'topic'} onClick={() => setTab('topic')}>
                Theo chủ đề
              </TabBtn>
              <TabBtn on={tab === 'wrong'} onClick={() => setTab('wrong')}>
                Câu đã sai ({p.due_for_review})
              </TabBtn>
            </div>

            {tab === 'topic' ? (
              done.length === 0 ? (
                <Note>Chưa luyện đủ câu nào để vẽ biểu đồ.</Note>
              ) : (
                <div className="space-y-3">
                  {done.map((t) => (
                    <TopicLine key={t.slug} t={t} showCount />
                  ))}
                </div>
              )
            ) : (
              <>
                <Note className="mb-3">
                  {p.due_for_review === 0
                    ? 'Không có câu nào cần ôn lại.'
                    : `${p.due_for_review} câu bạn đã trả lời sai và chưa làm lại đúng. Ôn lại để gỡ khỏi danh sách.`}
                </Note>
                <button
                  onClick={onGoWrong}
                  disabled={p.due_for_review === 0}
                  className="btn btn-primary"
                >
                  Ôn lại câu sai
                  <Icon name="arrow" size={18} />
                </button>
                <Note className="mt-2.5">
                  Bấm để xem danh sách từng câu và luyện lại đúng câu đó.
                </Note>
              </>
            )}

            <Label className="mt-6 mb-1.5">30 ngày qua</Label>
            <div className="card">
              <div className="flex h-16 items-end gap-0.5">
                {p.by_day.map((d) => (
                  <div
                    key={d.date}
                    // min-h-0.5 (2px) để ngày không luyện vẫn thấy là có cột —
                    // trống trơn thì tưởng dữ liệu thiếu.
                    className={'min-h-0.5 flex-1 rounded-t-sm ' + (d.answered > 0 ? 'bg-accent' : 'bg-canvas-2')}
                    style={{ height: Math.max(2, (d.answered / peak) * 100) + '%' }}
                    title={`${d.date}: ${d.answered} câu, ${d.correct} đúng`}
                  />
                ))}
              </div>
              <Between className="mt-1.5">
                <Note className="!text-xs">30 ngày trước</Note>
                <Note className="!text-xs">hôm nay</Note>
              </Between>
            </div>

            <div className="card mt-2 space-y-1 text-sm">
              <Row k="Tỉ lệ đúng tổng" v={accText(p.accuracy)} bold />
              <Row k="Đã trả lời" v={`${p.answered} câu`} />
              <Row k="Bài đã nộp" v={String(p.sessions_done)} />
              <Row k="Phạm vi" v={p.has_full ? 'toàn bộ kho' : 'câu mẫu'} />
            </div>
          </>
        )}
      </Screen>
    </Shell>
  )
}

function TabBtn({
  on,
  onClick,
  children,
}: {
  on: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      className={
        'flex-1 cursor-pointer rounded-lg py-1.5 text-sm font-semibold transition-shadow ' +
        (on ? 'bg-surface text-ink shadow-sm' : 'text-ink-faint hover:text-ink-soft')
      }
    >
      {children}
    </button>
  )
}

function Row({ k, v, bold }: { k: string; v: string; bold?: boolean }) {
  return (
    <Between>
      <span className="text-ink-soft">{k}</span>
      <span className={'font-mono ' + (bold ? 'font-semibold' : '')}>{v}</span>
    </Between>
  )
}

